import React, { FC, useEffect, useState } from 'react'
import { Alert, Button, Collapse, type CollapseProps, Form, Input, Popconfirm } from 'antd'
import { createSelector } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'

import { CustomModal } from '../../common/CustomModal'
import './index.less'
import { useAppDispatch, useAppSelector } from '../../../core/hooks'
import { CardinalApiState, markRecoveryKeyAsLost, provideRecoveryKey } from '../../../core/services/auth.api'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    recoveryKeyRequest: cardinalApi.recoveryKeyRequest,
  }),
)

interface RecoveryKeysFormType {
  recoveryKeys: { key: string }[]
}

export const ModalRecoveryKeyRequest: FC = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const dispatch = useAppDispatch()
  const { recoveryKeyRequest } = useAppSelector(reduxSelector)
  const isRecoveryKeyRequested = !!recoveryKeyRequest
  const isParentRequest = recoveryKeyRequest?.type === 'parent'
  const parentName = recoveryKeyRequest?.parentName
  const secondsToWaitBeforeAllowingSkip = 20
  const [seconds, setSeconds] = useState(0)

  const maxKeys = recoveryKeyRequest?.reasons.length ?? 1

  useEffect(() => {
    let i = 0 // local counter avoids any stale state issues
    const id = window.setInterval(() => {
      i += 1
      setSeconds(i)
      if (i >= 20) window.clearInterval(id)
    }, 1000)

    return () => window.clearInterval(id)
  }, [isRecoveryKeyRequested])

  const handleSubmit = (values: RecoveryKeysFormType) => {
    dispatch(provideRecoveryKey({ recoveryKeys: values.recoveryKeys.map((rk: { key: string }) => rk.key) }))
  }
  const getCustomFooter = () => {
    return [
      <Popconfirm
        key="confirmClose"
        title={t(isParentRequest ? 'user.recoveryKeyRequest.parentPopconfirm.title' : 'user.recoveryKeyRequest.popconfirm.title')}
        description={
          <div>
            <p>{t(isParentRequest ? 'user.recoveryKeyRequest.parentPopconfirm.description1' : 'user.recoveryKeyRequest.popconfirm.description1')}</p>
            <p>{t(isParentRequest ? 'user.recoveryKeyRequest.parentPopconfirm.description2' : 'user.recoveryKeyRequest.popconfirm.description2')}</p>
          </div>
        }
        onConfirm={() => dispatch(markRecoveryKeyAsLost())}
        okText={t(isParentRequest ? 'user.recoveryKeyRequest.parentPopconfirm.okText' : 'user.recoveryKeyRequest.popconfirm.okText')}
        okButtonProps={{ danger: true }}
        cancelText={t(isParentRequest ? 'user.recoveryKeyRequest.parentPopconfirm.cancelText' : 'user.recoveryKeyRequest.popconfirm.cancelText')}
        placement="topRight"
      >
        <Button loading={seconds < secondsToWaitBeforeAllowingSkip} key="back">
          {seconds < secondsToWaitBeforeAllowingSkip
            ? t('user.recoveryKeyRequest.skip_in_seconds', { seconds: secondsToWaitBeforeAllowingSkip - seconds })
            : t('user.recoveryKeyRequest.skip')}
        </Button>
      </Popconfirm>,

      <Button key="submit" type="primary" onClick={() => form.submit()}>
        {t('user.recoveryKeyRequest.submit')}
      </Button>,
    ]
  }
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: t('user.recoveryKeyRequest.collapse.item1.label'),
      children: <p>{t('user.recoveryKeyRequest.collapse.item1.text')}</p>,
    },
    {
      key: '2',
      label: t('user.recoveryKeyRequest.collapse.item2.label'),
      children: <p>{t('user.recoveryKeyRequest.collapse.item2.text')}</p>,
    },
    {
      key: '3',
      label: t('user.recoveryKeyRequest.collapse.item3.label'),
      children: (
        <p>
          {t('user.recoveryKeyRequest.collapse.item3.text.header')}
          <br />
          <strong>{t('user.recoveryKeyRequest.collapse.item3.text.content.line1.title')}</strong> – {t('user.recoveryKeyRequest.collapse.item3.text.content.line1.text')}
          <br />
          <strong>{t('user.recoveryKeyRequest.collapse.item3.text.content.line2.title')}</strong> – {t('user.recoveryKeyRequest.collapse.item3.text.content.line2.text')}
          <br />
          <strong>{t('user.recoveryKeyRequest.collapse.item3.text.content.line3.title')}</strong> – {t('user.recoveryKeyRequest.collapse.item3.text.content.line3.text')}
        </p>
      ),
    },
  ]

  return (
    <CustomModal
      isVisible={isRecoveryKeyRequested}
      title={isParentRequest ? t('user.recoveryKeyRequest.parentTitle', { parentName }) : t('user.recoveryKeyRequest.title')}
      customFooter={getCustomFooter()}
      closable={false}
    >
      <div className="modalRecoveryKeyRequest">
        <Alert
          type="warning"
          description={
            <div className="modalRecoveryKeyRequest__content">
              <h6>{t(isParentRequest ? 'user.recoveryKeyRequest.parentAlert.title' : 'user.recoveryKeyRequest.alert.title')}</h6>
              <div>
                <p>{t(isParentRequest ? 'user.recoveryKeyRequest.parentAlert.missingKeys' : 'user.recoveryKeyRequest.alert.missingKeys', { maxKeys, parentName })}</p>
                <p>{t(isParentRequest ? 'user.recoveryKeyRequest.parentAlert.instructions' : 'user.recoveryKeyRequest.alert.instructions')}</p>
              </div>
            </div>
          }
        />
        <Collapse size="small" items={items} />
        <Form
          className="modalRecoveryKeyRequest__form"
          layout="vertical"
          colon={false}
          form={form}
          onFinish={(values) => {
            handleSubmit(values)
          }}
          initialValues={{
            recoveryKeys: [{ key: '' }],
          }}
        >
          <Form.List
            name="recoveryKeys"
            rules={[
              {
                validator: async (_, recoveryKeys) => {
                  if (!recoveryKeys || recoveryKeys.length < 1) {
                    return Promise.reject(new Error(t('user.recoveryKeyRequest.form.recoveryKeyRequired')))
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'key']}
                      label={t('user.recoveryKeyRequest.form.recoveryKeyLabel', { index: name !== 0 ? '#' + (name + 1) : '' })}
                      rules={[{ required: true, message: t('user.recoveryKeyRequest.form.recoveryKeyRequired') }]}
                      style={{ width: '100%' }}
                    >
                      <Input
                        placeholder={t('user.recoveryKeyRequest.form.recoveryKeyLabel', { index: name !== 0 ? '#' + (name + 1) : '' })}
                        size="large"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>

                    {fields.length > 1 && (
                      <MinusCircleOutlined className="dynamic-delete-button" onClick={() => remove(name)} style={{ color: '#ff4d4f', fontSize: '16px', marginTop: '43px' }} />
                    )}
                  </div>
                ))}

                {fields.length < maxKeys && (
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      {t('user.recoveryKeyRequest.form.addKey', { remaining: maxKeys - fields.length })}
                    </Button>
                  </Form.Item>
                )}
              </>
            )}
          </Form.List>
        </Form>
      </div>
    </CustomModal>
  )
}
